import React, { useEffect } from 'react';
import PillTooltip from '../PillTooltip/PillTooltip';
import {
  PillRadioPickerContainer,
  PillRadioPickerLabel,
  PillRadioPickerOption,
  PillRadioPickerOptions,
  PillRadioPickerParent,
  PillRadioPickerParentLabel,
  PillRadioPickerSearch,
  PillRadioPickerWrapper,
} from './PillRadioPicker.styled';
import LabeledRadioButton from '../LabeledRadioButton/LabeledRadioButton';
import { isRoot, IRoot, useTreePattern } from '../../../helpers/hooks/useTreePattern';
import { FlexContainer } from '@emarketer/componentlibrary';
import { COLOR } from '../../../theme/constants';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IDropdownItem } from '../Dropdown/Dropdown';
import SearchBarDropdown from '../SearchBarDropdown/SearchBarDropdown';

export interface IPillRadioPicker {
  label: string;
  options: IRoot<IOption>;
  selectedValue?: string;
  callback: any;
  rootSelectable?: boolean;
  nonLeafOptionsSelectable?: boolean;
}

export interface IOption {
  name: string;
  subname?: string;
  value?: string;
}

const PillRadioPicker: React.FC<IPillRadioPicker> = (props) => {
  const { label, options, selectedValue, callback, nonLeafOptionsSelectable = true } = props;
  const [searchResults, setSearchResults] = React.useState<IDropdownItem[]>([]);
  const [show, setShow] = React.useState(false);

  const { currentNode, findNode, findNodesByKey, selectNode, toParentNode } =
    useTreePattern<IOption>(options);

  const handleSearch = (term) => {
    const searchRes: IDropdownItem[] = findNodesByKey(term).map((node) => {
      const label = node.value ? node.value.name : node.key;
      const value = node.value ? node.value.value : node.key;

      return {
        label: label,
        value: value,
      };
    });
    setSearchResults(searchRes);
  };

  const validSelection = findNode(selectedValue) !== null;

  const handleSelection = () => {
    setSearchResults([]);
    setShow(false);
  };

  const filterOptions =
    currentNode && currentNode.children.length
      ? currentNode.children.map((node) => {
          const value = node.value ? node.value.name : node.key;
          const value2 = node.value ? node.value.value : null;
          const selectable = node.children && node.children.length ? nonLeafOptionsSelectable : true;

          return (
            <PillRadioPickerOption
              key={node.key}
              flexDirection="row"
              justifyContent="space-between"
              spaceBetween={16}
              hideRadioButton={!selectable}
            >
              <LabeledRadioButton
                label={node.value.name}
                sublabel={node.value.subname}
                value={selectedValue && value === selectedValue ? 1 : 2}
                groupName={label}
                disabled={!selectable}
                callback={() => {
                  if (selectable) {
                    callback(node.key, value2);
                    if (!selectedValue || value !== selectedValue) handleSelection();
                  } else {
                    selectNode(value);
                  }
                }}
              />
              {node.children && node.children.length ? (
                <FontAwesomeIcon
                  icon={faChevronRight}
                  color={COLOR.BLUE}
                  width={8.75}
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectNode(value);
                  }}
                />
              ) : null}
            </PillRadioPickerOption>
          );
        })
      : 'Loading...';

  const handleDropdownCallback = (item) => {
    const [node, _] = findNode(item.label);
    if (!node) return;
    else if (node.children && node.children.length && !nonLeafOptionsSelectable) {
      selectNode(item.label);
    } else {
      callback(item.label, item.value);
      handleSelection();
    }
  };

  useEffect(() => {
    if (!show && searchResults.length) setSearchResults([]);
  }, [show]);

  return (
    <PillRadioPickerWrapper>
      <PillTooltip show={show} setShow={setShow} label={selectedValue || label} active={validSelection}>
        <PillRadioPickerContainer>
          <PillRadioPickerSearch>
            <SearchBarDropdown
              onChange={handleSearch}
              callback={handleDropdownCallback}
              items={searchResults}
              maxHeight="147px"
              showIcon
            />
          </PillRadioPickerSearch>
          {!isRoot(currentNode) && currentNode.parent ? (
            <PillRadioPickerParent flexDirection="row" justifyContent="start" spaceBetween={10}>
              <FontAwesomeIcon
                icon={faChevronLeft}
                color={COLOR.BLACK}
                width={8.75}
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  toParentNode();
                }}
              />
              <FlexContainer alignItems="start">
                <PillRadioPickerLabel>{label}</PillRadioPickerLabel>
                <PillRadioPickerParentLabel>{currentNode.value.name}</PillRadioPickerParentLabel>
              </FlexContainer>
            </PillRadioPickerParent>
          ) : null}
          <PillRadioPickerOptions alignItems="start" spaceBetween={11}>
            {filterOptions}
          </PillRadioPickerOptions>
        </PillRadioPickerContainer>
      </PillTooltip>
    </PillRadioPickerWrapper>
  );
};

export default PillRadioPicker;
